import Icon from './Icon.jsx';
import { formatDayLabel, timeRange } from '../lib/format.js';

/**
 * The booking lifecycle rendered as a live progress track:
 *   requested → confirmed → visit → completed → reviewed
 * A declined/cancelled booking breaks the track at the stage it stopped.
 */
// Icons deliberately avoid ticks and other "already done" imagery — a stage that
// is still being waited on must never read as complete. Completion is signalled
// only by the filled dot and its check glyph.
const STAGES = [
  { key: 'requested', label: 'Requested', icon: 'send', blurb: 'Sent to the nurse' },
  { key: 'confirmed', label: 'Confirmed', icon: 'clipboard', blurb: 'Nurse accepts' },
  { key: 'visit', label: 'Visit', icon: 'home', blurb: 'Care delivered' },
  { key: 'completed', label: 'Completed', icon: 'flag', blurb: 'Marked finished' },
  { key: 'reviewed', label: 'Reviewed', icon: 'star', blurb: 'Feedback given' },
];

/** Index of the last stage this booking has actually completed. */
function reachedIndex(booking) {
  if (booking.review_id) return 4;
  if (booking.status === 'completed') return 3;
  if (booking.status === 'accepted') return 1;
  return 0;
}

export default function WorkflowTracker({ booking, role = 'patient' }) {
  const broken = ['declined', 'cancelled'].includes(booking.status);
  const reached = reachedIndex(booking);
  // The stage the workflow is *waiting on* — one past the last completed one.
  const active = reached + 1;

  const nextAction = broken
    ? null
    : booking.status === 'pending'
      ? role === 'nurse'
        ? 'Accept or decline this request.'
        : `Waiting for ${booking.nurse_name} to respond.`
      : booking.status === 'accepted'
        ? role === 'nurse'
          ? `Visit ${booking.patient_name} on ${formatDayLabel(booking.date)}, ${timeRange(booking.start_time, booking.end_time)}. Mark it completed afterwards.`
          : `${booking.nurse_name} arrives ${formatDayLabel(booking.date).toLowerCase()} at ${timeRange(booking.start_time, booking.end_time)}.`
        : booking.status === 'completed' && !booking.review_id
          ? role === 'patient'
            ? 'Rate the visit to finish the booking.'
            : 'Waiting for the patient to leave a review.'
          : 'This booking is complete.';

  return (
    <div className="workflow" role="group" aria-label="Booking progress">
      <ol className="workflow-track">
        {STAGES.map((stage, i) => {
          const state = broken
            ? i <= reached
              ? 'done'
              : 'broken'
            : i <= reached
              ? 'done'
              : i === active
                ? 'active'
                : 'todo';
          return (
            <li key={stage.key} className="workflow-step" data-state={state}>
              <span className="workflow-dot">
                <Icon name={state === 'done' ? 'check' : state === 'broken' ? 'minus' : stage.icon} />
              </span>
              <span className="workflow-label">{stage.label}</span>
              <span className="workflow-blurb">{stage.blurb}</span>
            </li>
          );
        })}
      </ol>

      {broken ? (
        <div className="workflow-note broken">
          <Icon name="ban" />
          <span>
            This booking was <strong>{booking.status}</strong>
            {booking.decline_reason ? ` — “${booking.decline_reason}”` : '.'}
          </span>
        </div>
      ) : (
        nextAction && (
          <div className="workflow-note">
            <Icon name={booking.status === 'pending' ? 'hourglass' : 'arrow-right'} />
            <span>{nextAction}</span>
          </div>
        )
      )}
    </div>
  );
}
